/**
 * World-combat reactivity — pulse-kind × school × biome-FAMILY → per-layer-kind
 * transient responses (docs/plans/fx-grammar-specs/world-combat-reactivity.md).
 *
 * Third sibling of the data-driven mapping precedents: REACTION_MATRIX
 * (`${status}+${school}`) and DAY_RESPONSE (per-kind directional knobs). A
 * combat pulse (big cast / heavy impact / kill / reaction beat) resolves here
 * to envelope responses the AtmosphereReactivityDriver pushes into the
 * AtmosphereLayer's fixed pulse pool.
 *
 * Doctrine (spec §2.3): every response is a TRANSIENT multiplicative envelope —
 * factor starts at 1, peaks at 1 + amp, and always decays back to 1. There is
 * no code path that writes a preset value; the 🌗 tuner remains the sole owner
 * of persistent layer values.
 *
 * Keys are family-scoped, NOT per-biome: the 8-family partition reused from
 * BiomeTextureModule (`biomeFamilyOf`) — 31 per-biome rows would be authoring
 * drift bait. Key grammar (most-specific wins, mirroring the BiomeTextureModule
 * merge order + reactionMatrix school aliasing):
 *   `${kind}:${school}@${family}` > `${kind}@${family}` > `${kind}:${school}` > `${kind}`
 */
import { SCHOOL_ALIASES } from '../abilities/reactionMatrix.config';

/** The pulse-event vocabulary (spec §2.2). `playerDamaged` is reserved for the
 *  phase-2 vignette hook — no phase-1 response rows target it. */
export type ReactivityPulseKind = 'bigCast' | 'heavyImpact' | 'kill' | 'playerDamaged' | 'reactionBeat';

/** Envelope channels folded into tickModulation (scalar) or fanned out to a
 *  handle's `onPulse` hook at pulse time (positional). */
export type ReactivityChannel = 'opacity' | 'density' | 'speed' | 'tint' | 'positional';

export interface ReactivityResponse {
  /** EffectLayer kind targeted ('godRays', 'fireflies', …). */
  kind: string;
  channel: ReactivityChannel;
  /** Envelope peak delta (negative = recoil/thin), scaled by pulse intensity.
   *  For `positional` responses this is the kick-magnitude scalar passed to
   *  `onPulse` (× intensity), not a pooled envelope amp. */
  amp: number;
  /** Attack ramp duration (default REACTIVITY.defaultAttackMs). */
  attackMs?: number;
  /** Decay time constant — factor eases back toward 1 over ~4× this. */
  decayMs: number;
  /** Positional only — world-px pulse radius (default REACTIVITY.defaultRadiusPx). */
  radius?: number;
}

// ─── The response table (phase 1 — scalar channels + fireflies/critters
//     positional; fog-recoil / lightning-answer / vignette rows land with the
//     phase-2 hooks, each behind its own config row) ─────────────────────────
export const REACTIVITY_RESPONSE: Record<string, ReactivityResponse[]> = {
  // ── bigCast — a wound-up cast; the ambient glow inhales.
  bigCast: [
    { kind: 'glowBlobs', channel: 'opacity', amp: 0.25, decayMs: 600 },
  ],
  'bigCast:holy': [
    { kind: 'godRays', channel: 'opacity', amp: 0.6, decayMs: 900 },
  ],
  // ── heavyImpact — a landed blow; nearby life startles.
  heavyImpact: [
    { kind: 'fireflies', channel: 'positional', amp: 0.5, decayMs: 1200, radius: 260 },
    { kind: 'glowBlobs', channel: 'opacity', amp: 0.3, decayMs: 400 },
  ],
  'heavyImpact@temperate': [
    { kind: 'fireflies', channel: 'positional', amp: 0.6, decayMs: 1200, radius: 260 },
    { kind: 'lightField', channel: 'opacity', amp: -0.25, decayMs: 500 },
  ],
  'heavyImpact@hot': [
    { kind: 'weather', channel: 'density', amp: 0.5, decayMs: 600 },
    { kind: 'flicker', channel: 'opacity', amp: 0.45, decayMs: 500 },
    { kind: 'fireflies', channel: 'positional', amp: 0.5, decayMs: 1200, radius: 260 },
  ],
  // ── kill — always a beat (intensity fixed 1.0 at the emit site).
  kill: [
    { kind: 'critters', channel: 'positional', amp: 0.8, decayMs: 2000, radius: 320 },
    { kind: 'glowBlobs', channel: 'opacity', amp: 0.35, decayMs: 800 },
  ],
  'kill@dark': [
    { kind: 'fog', channel: 'opacity', amp: 0.5, decayMs: 1500 },
    { kind: 'critters', channel: 'positional', amp: 0.8, decayMs: 2000, radius: 320 },
  ],
  'kill@aquatic': [
    { kind: 'critters', channel: 'positional', amp: 1, decayMs: 2000, radius: 360 },
    { kind: 'caustics', channel: 'opacity', amp: 0.4, decayMs: 900 },
  ],
  // ── reactionBeat — a REACTION_MATRIX beat; the ambient glow flashes toward
  //    the layer's authored tintTo (setTint lerp — layers without tintTo are
  //    silently not driven, per the opt-in handle-hook pattern).
  reactionBeat: [
    { kind: 'glowBlobs', channel: 'tint', amp: 0.8, decayMs: 500 },
  ],
};

/** Phase-1 positional implementers — the kinds whose handles carry `onPulse`.
 *  Conformance-guarded (reactivity.config.test.ts): a positional row targeting
 *  a kind outside this set is authoring drift. Phase-2 adds fog / lightning /
 *  vignette here as their hooks land. */
export const POSITIONAL_PULSE_KINDS: ReadonlySet<string> = new Set(['fireflies', 'critters']);

export interface ReactivityConfig {
  /** Master switch (off → driver never starts, no pulses pool). */
  enabled: boolean;
  /** Cap on Σ|amp| across live scalar envelopes — incoming pulses over budget
   *  are scaled down to the remainder (never suddenly inert), floored then dropped. */
  budgetAmpSum: number;
  /** Per-pulse-kind cooldown — pulses inside the window MERGE into the live
   *  envelope (refresh decay at max amp) instead of stacking a new onset. */
  cooldownMs: Record<ReactivityPulseKind, number>;
  /** Global attack-onset ceiling per second — excess pulses skip their attack
   *  and splice into decay (photosensitivity guidance, spec §2.5). */
  maxOnsetsPerSec: number;
  /** Composed per-(kind,channel) factor clamp ceiling. */
  maxCompoundFactor: number;
  /** Composed factor floor — a recoil never blinks a layer fully out. */
  minCompoundFactor: number;
  /** Fixed envelope pool size (no per-pulse allocation). */
  poolSize: number;
  /** Default attack ramp (instant reads as a glitch, slow reads as unrelated). */
  defaultAttackMs: number;
  /** Default positional radius (world px). */
  defaultRadiusPx: number;
  /** Budget remainder below which an over-budget pulse is dropped outright. */
  budgetFloor: number;
  /** heavyImpact emit gate: damage at/above this (or any crit) pulses. */
  heavyImpactDamageThreshold: number;
  /** heavyImpact intensity = clamp(damage / norm, 0.3, 1) (maxHP proxy). */
  heavyImpactDamageNorm: number;
  /** bigCast intensity = clamp(0.4 + windUpMs / norm × 0.6, 0.4, 1). */
  bigCastWindUpNormMs: number;
}

export const REACTIVITY: ReactivityConfig = {
  enabled: true,
  budgetAmpSum: 3.0,
  cooldownMs: { bigCast: 250, heavyImpact: 250, kill: 600, playerDamaged: 400, reactionBeat: 250 },
  maxOnsetsPerSec: 3,
  maxCompoundFactor: 2.2,
  minCompoundFactor: 0.15,
  poolSize: 16,
  defaultAttackMs: 80,
  defaultRadiusPx: 260,
  budgetFloor: 0.1,
  heavyImpactDamageThreshold: 60,
  heavyImpactDamageNorm: 300,
  bigCastWindUpNormMs: 1500,
};

/**
 * Resolve the response list for a pulse — most-specific key wins:
 * `kind:school@family` > `kind@family` > `kind:school` > `kind`. School is
 * aliased through the reactionMatrix SCHOOL_ALIASES first (ice→frost parity),
 * so a wire damageType and a content school land the same row. Empty array =
 * the scene stays quiet for this pulse (not an error).
 */
export function resolveResponses(
  kind: ReactivityPulseKind,
  school?: string | null,
  family?: string | null,
  table: Record<string, ReactivityResponse[]> = REACTIVITY_RESPONSE,
): ReactivityResponse[] {
  const s = school ? (SCHOOL_ALIASES[school] ?? school) : null;
  const keys = [
    s && family ? `${kind}:${s}@${family}` : null,
    family ? `${kind}@${family}` : null,
    s ? `${kind}:${s}` : null,
    kind,
  ];
  for (const k of keys) {
    if (k && table[k]) return table[k];
  }
  return [];
}
